<template>
  <div Id="CommonLayout">
    <!-- 移动端遮罩层 -->
    <div 
      v-if="isMobile" 
      class="mobile-overlay" 
      :class="{ 'mobile-overlay-show': mobileMenuOpen }"
      @click="closeMobileMenu"
    ></div>
    
    <el-container style="min-height: 100vh;">
      <!-- 侧边栏 - 导航菜单 -->
      <el-aside 
        :width="sidebarWidth" 
        class="aside-container"
        :class="{ 'mobile-sidebar-open': mobileMenuOpen && isMobile }"
      >
        <div class="logo-container">
          <h1 class="logo-text">{{ appTitle }}</h1>
        </div>
        
        <!-- 导航菜单 -->
        <el-menu
          :default-active="activeMenu"
          class="el-menu-vertical-demo"
          @select="handleMenuSelect"
          background-color="#0f172a"
          text-color="#bfc6d4"
          active-text-color="#ffffff"
          :unique-opened="false"
        >
          <!-- 动态渲染有权限的路由菜单 -->
          <template v-for="route in permissionRoutes" :key="route.path">
            <!-- 跳过Layout根路由 -->
            <template v-if="route.name === 'Layout' && route.children && route.children.length">
              <!-- 直接渲染Layout的子路由 -->
              <template v-for="childRoute in route.children" :key="childRoute.path">
                <!-- 有子路由的菜单 -->
                <el-sub-menu 
                  v-if="childRoute.children && childRoute.children.length && !childRoute.meta?.hidden" 
                  :index="childRoute.path"
                >
                  <template #title>
                    <el-icon :size="18">
                      <component :is="getIcon(childRoute.meta?.icon)" />
                    </el-icon>
                    <span>{{ childRoute.meta?.title }}</span>
                  </template>
                  
                  <!-- 渲染二级子路由 -->
                  <template v-for="grandChild in childRoute.children" :key="grandChild.path">
                    <el-menu-item 
                      :index="`${childRoute.path}/${grandChild.path}`"
                      v-if="!grandChild.meta?.hidden"
                    >
                      <!-- <el-icon :size="16">
                        <component :is="getIcon(grandChild.meta?.icon)" />
                      </el-icon> -->
                      <span>{{ grandChild.meta?.title }}</span>
                    </el-menu-item>
                  </template>
                </el-sub-menu>
              </template>
            </template>
          </template>
        </el-menu>
      </el-aside>
      
      <!-- 主内容区域 -->
      <el-container>
        <!-- 头部区域 - 包含面包屑和用户信息 -->
        <el-header class="header-container">
          <!-- 移动端菜单按钮 -->
          <button 
            v-if="isMobile"
            class="mobile-menu-btn"
            @click="toggleMobileMenu"
            aria-label="切换菜单"
            :title="`菜单${mobileMenuOpen ? '已打开' : '已关闭'}`"
          >
            <el-icon :size="18">
              <Menu />
            </el-icon>
          </button>
          
          <!-- 调试信息（开发环境显示） -->
          <div 
            v-if="isDev" 
            style="position: fixed; top: 10px; right: 10px; background: rgba(0,0,0,0.8); color: white; padding: 8px; border-radius: 4px; font-size: 12px; z-index: 9999;"
          >
            屏幕: {{ screenWidth }}px<br>
            移动端: {{ isMobile ? '是' : '否' }}<br>
            菜单: {{ mobileMenuOpen ? '开' : '关' }}
          </div>
          
          <div class="breadcrumb-container">
            <!-- 面包屑导航 -->
            <el-breadcrumb separator="/">
              <el-breadcrumb-item 
                v-for="(crumb, index) in breadcrumbs" 
                :key="index"
                :replace="true"
              >
                {{ crumb.title }}
              </el-breadcrumb-item>
            </el-breadcrumb>
          </div>
          
          <div class="user-info">
            <el-dropdown @command="handleCommand">
              <span class="user-name">
                <el-avatar :size="32" icon="User" />
                <span style="margin-left: 8px;">{{ userInfo.schoolName }}</span>
                <el-icon :size="16" style="margin-left: 4px;"><ArrowDown /></el-icon>
              </span>
              <template #dropdown>
                <el-dropdown-menu>
                  <!-- <el-dropdown-item>个人中心</el-dropdown-item>
                  <el-dropdown-item>修改密码</el-dropdown-item> -->
                  <el-dropdown-item command="Logout">退出登录</el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </div>
        </el-header>
        
        <!-- 页面主内容 -->
        <el-main class="main-content">
          <router-view />
        </el-main>
      </el-container>
    </el-container>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { usePermissionStore } from '@/stores/Permission';
import { Menu, TrendCharts, ArrowDown } from '@element-plus/icons-vue';
import { getAllPermissionTypes } from '@/router/types';
import { ElMessage, ElMessageBox } from 'element-plus'
import {
  post_Login_Out, // 退出登录
} from '@/Api/Login';
// 定义图标类型（只包含需要的图标）
type IconType = 'Menu' | 'TrendCharts';

// 图标映射表（仅保留需要的图标）
const iconMap = {
  Menu,
  TrendCharts,
  ArrowDown 
};
const appTitle = ref(import.meta.env.VITE_APP_TITLE || '');

const userInfo = ref(JSON.parse(sessionStorage.getItem('userInfo') || '{}'));

// 移动端相关状态 - 立即检测初始状态
const checkIsMobile = () => {
  return typeof window !== 'undefined' ? window.innerWidth <= 768 : false;
};

const isMobile = ref(checkIsMobile());
const mobileMenuOpen = ref(false);

// 检测是否为移动端的更新函数
const updateMobileState = () => {
  const newIsMobile = checkIsMobile();
  isMobile.value = newIsMobile;
  
  // 屏幕变大时自动关闭移动端菜单
  if (!newIsMobile) {
    mobileMenuOpen.value = false;
  }
  
  console.log('移动端状态更新:', { 
    screenWidth: window.innerWidth, 
    isMobile: newIsMobile,
    mobileMenuOpen: mobileMenuOpen.value
  });
};

// 计算侧边栏宽度
const sidebarWidth = computed(() => {
  return isMobile.value ? '250px' : '200px';
});

// 开发环境判断
const isDev = computed(() => {
  return import.meta.env.DEV;
});

// 屏幕宽度
const screenWidth = computed(() => {
  return typeof window !== 'undefined' ? window.innerWidth : 0;
});

// 切换移动端菜单
const toggleMobileMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value;
  console.log('切换菜单:', mobileMenuOpen.value);
};

// 关闭移动端菜单
const closeMobileMenu = () => {
  mobileMenuOpen.value = false;
};
// 安全获取图标组件，解决类型问题
const getIcon = (iconName?: string) => {
  // 只允许使用指定的图标类型
  if (iconName && (iconName === 'Menu' || iconName === 'TrendCharts')) {
    return iconMap[iconName as IconType];
  }
  // 默认使用Menu图标
  return iconMap.Menu;
};

// 获取路由和路由器实例
const route = useRoute();
const router = useRouter();

// 获取权限存储
const permissionStore = usePermissionStore();

// 面包屑导航数据
const breadcrumbs = ref<any[]>([]);

// 计算当前激活的菜单
const activeMenu = computed(() => {
  return route.path;
});

// 计算有权限的路由
const permissionRoutes = computed(() => {
  return permissionStore.accessRoutes;
});

// 处理菜单选择事件
const handleMenuSelect = (index: string) => {
  router.push(index);
  // 移动端选择菜单后自动关闭侧边栏
  if (isMobile.value) {
    closeMobileMenu();
  }
};

// 更新面包屑导航
const updateBreadcrumbs = () => {
  // 过滤条件可能导致面包屑屑第一项路径异常
  const matched = route.matched.filter(item => 
    item.meta && item.meta.title && item.name !== 'Layout'
  );
  breadcrumbs.value = matched.map(item => ({
    title: item.meta?.title,
    path: item.path
  }));
};

/**
 * @date: 2025-08-29 13:13:14
 * @description: 下拉菜单点击事件
 * @modifyContent:
 * @author: Forever丿顾北
**/
const handleCommand = async (command: string) => {
  console.log('asasas', command)
  if (command === 'Logout') {
    ElMessageBox.confirm(
    '确定退出登录吗？',
    '提示',
    {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'error',
    }
  )
    .then(async () => {
      try {
        const response = await post_Login_Out({
          Authorization: userInfo.value.Authorization
        });
        console.log('退出登录成功:', response);
        if (response.code === 200) {
          ElMessage({
            type: 'success',
            message: '退出登录成功',
            onClose: () => {
              // 1. 清除用户信息
              sessionStorage.removeItem('userInfo');
              // 2. 跳转到登录页
              router.push('/login');
              // 3. 刷新页面
              window.location.reload();
            }
          })
        }
      } catch (error) {
        console.error('退出登录失败:', error);
      }
    })
    .catch(() => {
      ElMessage({
        type: 'info',
        message: '已取消退出',
      })
    })
    // 退出登录
    
  }
}

// 监听路由变化，更新面包屑
watch(
  () => route.path,
  () => {
    updateBreadcrumbs();
  }
);

// 初始化
onMounted(() => {
  console.log('加载的权限路由:', permissionRoutes.value);
  if (permissionStore.accessRoutes.length === 0) {
    permissionStore.setPermissions(getAllPermissionTypes());
    updateBreadcrumbs();
  } else {
    updateBreadcrumbs();
  }
  
  // 初始检测屏幕尺寸
  updateMobileState();
  // 监听窗口大小变化
  window.addEventListener('resize', updateMobileState);
});

// 清理事件监听器
onUnmounted(() => {
  window.removeEventListener('resize', updateMobileState);
});
</script>

<style scoped lang="scss">

</style>
